import { useState, useEffect } from 'react';

const INITIAL_VALUES = { x: 0, y: 0 };

// opts: initialCoord = {x, y}, onMove, minX, minY
export const useDraggable = (ref, opts = {}) => {
  const { minX = -Infinity, minY = -Infinity } = opts;
  const [location, setLocation] = useState(opts.initialCoord || INITIAL_VALUES);
  const reset = () => {
    setLocation(INITIAL_VALUES);
    if (ref) ref.current.dispatchEvent(new Event('reset'));
    if (opts.onMove) opts.onMove(INITIAL_VALUES);
  };

  useEffect(() => {
    let active = false;
    let initialX;
    let initialY;
    let xOffset = location.x;
    let yOffset = location.y;

    const dragStart = e => {
      if (e.target === ref.current) {
        active = true;

        if (e.type === 'touchstart') {
          initialX = e.touches[0].clientX - xOffset;
          initialY = e.touches[0].clientY - yOffset;
        } else {
          initialX = e.clientX - xOffset;
          initialY = e.clientY - yOffset;
        }

        ref.current.style.cursor = 'grabbing'; // eslint-disable-line
      }
    };

    const drag = e => {
      if (active) {
        let currentX;
        let currentY;
        e.preventDefault();

        if (e.type === 'touchmove') {
          currentX = Math.max(minX, e.touches[0].clientX - initialX);
          currentY = Math.max(minY, e.touches[0].clientY - initialY);
        } else {
          currentX = Math.max(minX, e.clientX - initialX);
          currentY = Math.max(minY, e.clientY - initialY);
        }

        xOffset = currentX;
        yOffset = currentY;
        const payload = { x: currentX, y: currentY };

        setLocation(payload);
        if (opts.onMove) opts.onMove(payload);
      }
    };

    const dragEnd = () => {
      active = false;
      ref.current.style.cursor = 'grab'; // eslint-disable-line
    };

    const resetEvent = () => {
      dragEnd();
      xOffset = 0;
      yOffset = 0;
    };

    if (ref) {
      document.body.addEventListener('touchstart', dragStart, false);
      document.body.addEventListener('touchend', dragEnd, false);
      document.body.addEventListener('touchmove', drag, false);
      document.body.addEventListener('mousedown', dragStart, false);
      document.body.addEventListener('mouseup', dragEnd, false);
      document.body.addEventListener('mousemove', drag, false);
      ref.current.addEventListener('reset', resetEvent, false);
    }

    return () => {
      if (!ref) return;
      document.body.removeEventListener('touchstart', dragStart);
      document.body.removeEventListener('touchend', dragEnd);
      document.body.removeEventListener('touchmove', drag);
      document.body.removeEventListener('mousedown', dragStart);
      document.body.removeEventListener('mouseup', dragEnd);
      document.body.removeEventListener('mousemove', drag);
    };
  }, []);

  return { location, reset };
};
